/**
 * CalendarCampaignStep
 * 
 * Self-contained step used inside the renewal campaign wizard when
 * "Google Calendar" type is selected.  It handles the entire Google Calendar
 * connection → calendar selection → campaign builder flow without the user
 * having to leave the /renewals page.
 *
 * States:
 *  1. checking  — verifying calendar auth
 *  2. needs_auth — Google OAuth not done yet
 *  3. needs_calendar — auth ok but no active calendar
 *  4. ready — auth + active calendar → show CalendarCampaignBuilder
 */

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { calendarService, CalendarListEntry, CalendarCampaign } from "../../services/calendarService";
import CalendarCampaignBuilder from "../CalendarCampaignBuilder";
import { Loader2, Calendar, Check, ExternalLink, Info } from "lucide-react";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
    CALENDAR_CAMPAIGN_CONTEXT,
    isCalendarOAuthMessageContext,
} from "@/lib/calendarCampaignFlow";
import { startCalendarCampaignOAuth } from "@/lib/calendarOAuth";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

interface Props {
    onCreated: (campaign?: CalendarCampaign) => void;
    onCancel: () => void;
    onStepChange?: (step: number) => void;
    initialCampaignName?: string;
}

type FlowState = "checking" | "needs_auth" | "needs_calendar" | "ready";

const CalendarCampaignStep: React.FC<Props> = ({ onCreated, onCancel, onStepChange, initialCampaignName }) => {
    const { token } = useAuth();
    const [flowState, setFlowState] = useState<FlowState>("checking");
    const [calendars, setCalendars] = useState<CalendarListEntry[]>([]);
    const [campaigns, setCampaigns] = useState<CalendarCampaign[]>([]);
    const [authUrl, setAuthUrl] = useState<string | null>(null);
    const [activating, setActivating] = useState<string | null>(null);
    const [loadingCalendars, setLoadingCalendars] = useState(false);

    // ── Fetch the OAuth URL (needed for the connect button) ────────────────
    const fetchAuthUrl = useCallback(async () => {
        try {
            const res = await axios.get(`${BACKEND}/google-sheets/auth?context=${CALENDAR_CAMPAIGN_CONTEXT}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                withCredentials: true,
            });
            if (res.data?.authorization_url) {
                setAuthUrl(res.data.authorization_url);
            }
        } catch (e) {
            console.error("Error fetching calendar auth URL", e);
        }
    }, [token]);

    // ── Load calendars and determine flow state ────────────────────────────
    const loadCalendars = useCallback(async () => {
        setLoadingCalendars(true);
        try {
            const list = await calendarService.listCalendars();
            setCalendars(list || []);
            if ((list || []).some((c: CalendarListEntry) => c.is_active)) {
                setFlowState("ready");
            } else {
                setFlowState("needs_calendar");
            }
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 401 || status === 403 || status === 500) {
                // Not authenticated with Google Calendar
                setFlowState("needs_auth");
                fetchAuthUrl();
            } else {
                toast.error("Error al cargar los calendarios");
                setFlowState("needs_calendar");
            }
        } finally {
            setLoadingCalendars(false);
        }
    }, [fetchAuthUrl]);

    // ── Load campaigns for calendar selection validation ───────────────────
    useEffect(() => {
        calendarService.getCalendarCampaigns()
            .then(data => setCampaigns(data || []))
            .catch(() => setCampaigns([]));
    }, []);

    // ── On mount: check for OAuth return or initial state ─────────────────
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Check origin for security
            if (event.origin !== window.location.origin) return;
            
            if (event.data?.type === 'google_oauth_success' && isCalendarOAuthMessageContext(event.data?.context)) {
                console.log('Google OAuth success message received');
                toast.success("Google Calendar conectado correctamente");
                loadCalendars();
            }
        };

        window.addEventListener('message', handleMessage);
        
        // Check for legacy URL param (fallback)
        const params = new URLSearchParams(window.location.search);
        const authStatus = params.get("auth");

        if (authStatus === "success") {
            const url = new URL(window.location.href);
            url.searchParams.delete("auth");
            window.history.replaceState({}, "", url.toString());
            toast.success("Google Calendar conectado correctamente");
            loadCalendars();
        } else {
            loadCalendars();
        }

        return () => window.removeEventListener('message', handleMessage);
    }, [loadCalendars]);

    // ── Toggle calendar active state ───────────────────────────────────────
    const activateCalendar = async (calendarId: string, currentActive: boolean) => {
        setActivating(calendarId);
        try {
            const newState = !currentActive;

            await axios.post(`${BACKEND}/api/calendar/activate`, {
                calendar_id: calendarId,
                active: newState,
            }, {
                withCredentials: true,
            });

            const updated = calendars.map(c =>
                c.id === calendarId ? { ...c, is_active: newState } : c
            );
            setCalendars(updated);

            if (newState) {
                toast.success("Calendario conectado");
                setFlowState("ready");
            } else {
                const stillHasActive = updated.some(c => c.is_active);
                if (!stillHasActive) setFlowState("needs_calendar");
            }
        } catch (err: any) {
            const msg = err?.response?.data?.detail || "No se pudo actualizar el calendario";
            toast.error(msg);
        } finally {
            setActivating(null);
        }
    };

    // ── Connect: same tab as Google Sheets (no popup) ─────────────────────
    const handleConnect = async () => {
        await startCalendarCampaignOAuth(CALENDAR_CAMPAIGN_CONTEXT, initialCampaignName);
    };
    
    // ── UI States ── 
    const [showInstructions, setShowInstructions] = useState(false);

    // ── Render ─────────────────────────────────────────────────────────────

    if (flowState !== "ready") {
        return (
            <Card className="mt-6">
                <CardContent className="space-y-6 p-6">
                    {/* Header */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-lg font-semibold text-gray-900">Configurar Campaña Calendar</div>
                            <p className="text-xs text-gray-400">Conecta tu cuenta de Google Calendar y elige el calendario</p>
                        </div>
                    </div>

                    {/* Guía Paso a Paso */}
                    <div className="mt-6 relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                        <div className="flex items-start gap-5 pl-4">
                            <div className="flex-1">
                                <h4 className="text-lg font-semibold text-gray-900 mb-1">Prepara tu calendario de Google</h4>
                                <p className="text-xs text-gray-600 leading-relaxed mb-4">
                                    Antes de continuar, asegúrate de que tus eventos tengan el número de teléfono en la descripción configurado correctamente.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => window.open("https://calendar.google.com", "_blank", "noopener,noreferrer")}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all animate-[pulse_2s_ease-in-out_infinite] hover:animate-none"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                    Ver guía paso a paso
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Contenedor de Configuración (sub-card con acento izquierdo) */}
                    <Card className="mt-4 group relative rounded-xl border border-gray-200 bg-white p-4 mb-4 shadow-[0_8px_30px_rgba(12,20,30,0.06)]">
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                        <CardContent className="pl-4 space-y-5">
                            <div>
                                <label className="text-lg font-semibold mb-0 block">Selecciona el calendario</label>
                                <div className="text-xs text-gray-500 mb-3">Escoge de dónde extraeremos los eventos.</div>

                                {(flowState === "checking" || loadingCalendars) && (
                                    <div className="text-center py-10">
                                        <Loader2 className="w-8 h-8 animate-spin text-[#1CB3B3] mx-auto mb-4" />
                                        <p className="text-gray-500 text-sm">Verificando conexión con Google Calendar…</p>
                                    </div>
                                )}

                                {flowState === "needs_auth" && !loadingCalendars && (
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 rounded-full bg-[#e6f7f4] flex items-center justify-center mx-auto mb-4 border border-[#e0f2f1]">
                                            <Calendar className="w-8 h-8 text-[#1CB3B3]" />
                                        </div>
                                        <h3 className="font-medium mb-2 text-gray-900">Conecta tu Google Calendar</h3>
                                        <p className="text-sm text-gray-600 max-w-sm mx-auto mb-5">
                                            Para extraer los eventos necesitas autorizar el acceso a tu Google Calendar. Solo tomará un momento.
                                        </p>
                                        <div className="flex justify-center gap-3 mb-6">
                                            <Button
                                                onClick={handleConnect}
                                                className="max-w-full bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold text-sm px-4 h-10 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 flex items-center justify-center gap-2 whitespace-normal text-center"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5 shrink-0" /> Conectar con Google
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {flowState === "needs_calendar" && !loadingCalendars && (
                                    <div className="space-y-3 max-h-[300px] overflow-y-auto mt-4 pr-1">
                                        {calendars.length === 0 ? (
                                            <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                                                <Info className="w-4 h-4 flex-shrink-0 text-amber-500" />
                                                No hay calendarios en tu cuenta de Google.
                                            </div>
                                        ) : (
                                            calendars.map(cal => (
                                                <div
                                                    key={cal.id}
                                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-2 rounded-xl hover:bg-gray-50 transition-colors bg-white border-gray-200"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0 w-full sm:flex-1">
                                                        <div className="w-10 h-10 rounded-full bg-[#e6f7f4] flex items-center justify-center flex-shrink-0">
                                                            <Calendar className="w-5 h-5 text-[#1CB3B3]" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-sm text-gray-900 whitespace-normal break-words leading-snug" title={cal.summary}>
                                                                {cal.summary}
                                                            </p>
                                                            {cal.primary && (
                                                                <span className="text-[10px] uppercase tracking-wider text-[#1CB3B3] bg-[#e6f7f4] px-2 py-0.5 rounded-full font-bold inline-block mt-1">
                                                                    Principal
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {activating === cal.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin text-[#1CB3B3]" />
                                                    ) : cal.is_active ? (
                                                        <div className="flex items-center gap-1.5 text-xs font-medium text-[#1CB3B3] bg-[#e6f7f4] px-3 py-1.5 rounded-full">
                                                            <Check className="w-3 h-3" /> Activo
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => activateCalendar(cal.id, cal.is_active || false)}
                                                            disabled={!!activating}
                                                            className="text-xs font-medium text-white bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#189999] hover:to-[#147a7a] px-4 py-2 rounded-full transition-all disabled:opacity-50"
                                                        >
                                                            Conectar
                                                        </button>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </CardContent>

                {/* CardFooter — mismo estilo que DynamicConfigStep */}
                <CardFooter className="pt-6 border-t border-gray-100">
                    <div className="w-full flex justify-between items-center">
                        <Button
                            variant="outline"
                            onClick={onCancel}
                            className="border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm"
                        >
                            ← Atrás
                        </Button>
                        <Button
                            onClick={() => {
                                if (flowState === "needs_auth") toast.error("Para continuar, primero debes conectar tu Google Calendar.");
                                else toast.error("Por favor, conecta al menos un calendario de la lista para continuar.");
                            }}
                            className="bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 disabled:opacity-50"
                        >
                            Siguiente →
                        </Button>
                    </div>
                </CardFooter>
            </Card>
        );
    }

    // flowState === "ready" — auth + active calendar exist
    return (
        <CalendarCampaignBuilder
            calendars={calendars}
            campaigns={campaigns}
            onCreated={(campaign) => {
                onCreated(campaign);
            }}
            onCancel={onCancel}
            onStepChange={onStepChange}
            initialCampaignName={initialCampaignName}
        />
    );
};

export default CalendarCampaignStep;

